import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  fetchVariants,
  fetchVariant,
  createVariant,
  createVariantsBulk,
  updateVariant,
  deleteVariant,
  type FetchVariantsOptions,
} from "../api/variantApi";

// Re-export types for convenience
export type {
  Variant,
  VariantsResponse,
  FetchVariantsOptions,
  CreateVariantData,
  UpdateVariantData,
  BulkVariantItem,
} from "../api/variantApi";

// Query Keys
export const variantKeys = {
  all: ["variants"] as const,
  lists: () => [...variantKeys.all, "list"] as const,
  list: (options: Omit<FetchVariantsOptions, "page">) => [...variantKeys.lists(), options] as const,
  infinite: (options: Omit<FetchVariantsOptions, "page">) =>
    [...variantKeys.all, "infinite", options] as const,
  details: () => [...variantKeys.all, "detail"] as const,
  detail: (id: string) => [...variantKeys.details(), id] as const,
};

// Hooks

/**
 * Fetch variants with pagination (standard query)
 */
export const useVariants = (options: FetchVariantsOptions) => {
  return useQuery({
    queryKey: variantKeys.list(options),
    queryFn: () => fetchVariants(options),
    enabled: !!options.branch,
  });
};

/**
 * Fetch variants with infinite scroll
 */
export const useInfiniteVariants = (options: Omit<FetchVariantsOptions, "page">) => {
  return useInfiniteQuery({
    queryKey: variantKeys.infinite(options),
    queryFn: ({ pageParam = 1 }) => fetchVariants({ ...options, page: pageParam }),
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.pages) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: !!options.branch,
  });
};

/**
 * Fetch a single variant by ID
 */
export const useVariant = (id: string) => {
  return useQuery({
    queryKey: variantKeys.detail(id),
    queryFn: () => fetchVariant(id),
    enabled: !!id,
  });
};

/**
 * Create a variant mutation
 */
export const useCreateVariant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createVariant,
    onSuccess: () => {
      toast.success("Success!", {
        description: "Variant has been added successfully",
      });
      // Invalidate variant lists to refetch
      queryClient.invalidateQueries({ queryKey: variantKeys.lists() });
      queryClient.invalidateQueries({ queryKey: variantKeys.all });
      // Also invalidate product details since variants are linked to products
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
    onError: (error: Error) => {
      toast.error("Error", {
        description: error.message || "Failed to create variant",
      });
    },
  });
};

/**
 * Bulk create variants mutation
 */
export const useCreateVariantsBulk = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createVariantsBulk,
    onSuccess: () => {
      toast.success("Success!", {
        description: "Variants have been added successfully",
      });
      // Invalidate variant lists to refetch
      queryClient.invalidateQueries({ queryKey: variantKeys.lists() });
      queryClient.invalidateQueries({ queryKey: variantKeys.all });
      // Also invalidate product details
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
    onError: (error: Error) => {
      toast.error("Error", {
        description: error.message || "Failed to create variants",
      });
    },
  });
};

/**
 * Update a variant mutation
 */
export const useUpdateVariant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateVariant,
    onSuccess: () => {
      toast.success("Success!", {
        description: "Variant has been updated successfully",
      });
      // Invalidate variant lists to refetch
      queryClient.invalidateQueries({ queryKey: variantKeys.lists() });
      queryClient.invalidateQueries({ queryKey: variantKeys.all });
      // Also invalidate product details
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
    onError: (error: Error) => {
      toast.error("Error", {
        description: error.message || "Failed to update variant",
      });
    },
  });
};

/**
 * Delete a variant mutation
 */
export const useDeleteVariant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteVariant,
    onSuccess: () => {
      toast.success("Success!", {
        description: "Variant has been deleted successfully",
      });
      // Invalidate variant lists to refetch
      queryClient.invalidateQueries({ queryKey: variantKeys.lists() });
      queryClient.invalidateQueries({ queryKey: variantKeys.all });
      // Also invalidate product details
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
    onError: (error: Error) => {
      toast.error("Error", {
        description: error.message || "Failed to delete variant",
      });
    },
  });
};
